import { useEffect } from "react";

/**
 * Ensure page-level SEO meta tags (title, description, canonical, og:*, twitter:*)
 * are set on route change. Used by every page to prevent stale meta after SPA nav.
 *
 * Also clears any JSON-LD nodes injected by other pages via ids in `clearLdIds`.
 */
export function useDocumentMeta({
  title,
  description,
  canonical,
  ogImage,
  clearLdIds = [],
}) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const upsertLink = (rel, hrefValue, hreflang) => {
      if (!hrefValue) return;
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute("href", hrefValue);
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertLink("canonical", canonical);
    upsertLink("alternate", canonical, "it");
    if (canonical) {
      upsertLink(
        "alternate",
        canonical + (canonical.includes("?") ? "&" : "?") + "lang=en",
        "en"
      );
      upsertLink("alternate", canonical, "x-default");
    }

    // Remove any per-page JSON-LD injected by other routes
    clearLdIds.forEach((id) => {
      const n = document.getElementById(id);
      if (n) n.remove();
    });
  }, [title, description, canonical, ogImage, clearLdIds]);
}
